// ─── Supported Languages ──────────────────────────────────────────────────────
export const SUPPORTED_LANGUAGES = [
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱', nativeName: 'Nederlands' },
  { code: 'en', label: 'English', flag: '🇬🇧', nativeName: 'English' },
  { code: 'fr', label: 'Français', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'es', label: 'Español', flag: '🇪🇸', nativeName: 'Español' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹', nativeName: 'Italiano' },
] as const;

export type LanguageCode = 'nl' | 'en' | 'fr' | 'es' | 'it';

// ─── Translation Dictionary ───────────────────────────────────────────────────
const translations: Record<LanguageCode, Record<string, string>> = {
  nl: {
    // Navigation groups
    'nav.kassa': 'Kassa',
    'nav.beheer': 'Beheer',
    'nav.promoties_sectie': 'Promoties',
    'nav.kortingscodes': 'Kortingscodes',
    'nav.happy_hour': 'Happy Hour',
    'nav.operatie': 'Operatie',
    'nav.configuratie': 'Configuratie',
    'nav.rapportages': 'Rapportages',
    // Nav items
    'nav.pos_terminal': 'POS Terminal',
    'nav.handheld': 'Handheld',
    'nav.bediening_tablet': 'Bediening Tablet',
    'nav.keuken_display': 'Keuken Display',
    'nav.live_keuken_queue': 'Live Keuken Queue',
    'nav.ayce_keuken': 'AYCE Keuken',
    'nav.keuken_analytics': 'Keuken Analytics',
    'nav.bar_display': 'Bar Display',
    'nav.dashboard': 'Dashboard',
    'nav.restaurant_prestaties': 'Restaurant Prestaties',
    'nav.menukaart': 'Menukaart',
    'nav.gangen_beheer': 'Gangen beheer',
    'nav.menu_preview': 'Menu Preview',
    'nav.ayce_preview': 'AYCE Preview',
    'nav.all_you_can_eat': 'All You Can Eat',
    'nav.product_database': 'Product Database',
    'nav.vloerplan_editor': 'Vloerplan Editor',
    'nav.zones_tafels': 'Zones & Tafels',
    'nav.medewerkers': 'Medewerkers',
    'nav.medewerkersbeheer': 'Medewerkersbeheer',
    'nav.promoties': 'Promoties',
    'nav.print_display': 'Print & Display',
    'nav.bestelgeschiedenis': 'Bestelgeschiedenis',
    'nav.offline_bonnen': 'Offline Bonnen',
    'nav.klanten': 'Klanten',
    'nav.facturen': 'Facturen',
    'nav.instellingen': 'Instellingen',
    'nav.testdata': 'Testdata',
    'nav.pre_launch': 'Pre-Launch Validatie',
    'nav.data_export': 'Data Export',
    'nav.export_rapporten': 'Export Rapporten',
    'nav.statistieken': 'Statistieken',
    'nav.z_rapport': 'Z-Rapport',
    'nav.btw_overzicht': 'BTW-overzicht',
    // Sync
    'sync.synchroniseren': 'Synchroniseren...',
    'sync.wachtend': 'wachtend',
    'sync.online': 'Online',
    'sync.offline_gesync': 'Offline · Gesync',
    'sync.synchronisatie': 'Synchronisatie',
    'sync.modus': 'Modus',
    'sync.offline': 'Offline',
    'sync.laatste_sync': 'Laatste sync',
    'sync.nooit': 'Nooit',
    'sync.zojuist': 'Zojuist',
    'sync.geleden': 'geleden',
    'sync.wachtende': 'Wachtend',
    'sync.wijziging': 'wijziging',
    'sync.wijzigingen': 'wijzigingen',
    'sync.nu_sync': 'Nu sync',
    'sync.inklappen': 'Inklappen',
    'sync.uitloggen': 'Uitloggen',
    // Common
    'common.opslaan': 'Opslaan',
    'common.opslaan_ellipsis': 'Opslaan...',
    'common.opgeslagen': 'Opgeslagen!',
    'common.annuleren': 'Annuleren',
    'common.toevoegen': 'Toevoegen',
    'common.bewerken': 'Bewerken',
    'common.verwijderen': 'Verwijderen',
    'common.actief': 'Actief',
    'common.inactief': 'Inactief',
    'common.nieuw': 'Nieuw',
    'common.zoeken': 'Zoeken',
    'common.laden': 'Laden...',
    'common.sluiten': 'Sluiten',
    'common.bevestigen': 'Bevestigen',
    'common.ja': 'Ja',
    'common.nee': 'Nee',
    'common.taal': 'Taal',
    'common.talen': 'Talen',
    // Settings tabs
    'settings.bedrijfsprofiel': 'Bedrijfsprofiel',
    'settings.zones': 'Zones',
    'settings.apparaten': 'Apparaten',
    'settings.categorieen': 'Categorieën',
    'settings.rollen': 'Personeelsrollen',
    'settings.ayce': 'AYCE Instellingen',
    'settings.btw': 'BTW Groepen',
    'settings.verbinding': 'Verbinding & Sync',
    'settings.talen': 'Talen & Vertalingen',
    // Language settings
    'lang.title': 'Talen & Vertalingen',
    'lang.subtitle': 'Beheer de taalinstellingen voor de software en de menukaart.',
    'lang.ui_title': 'Software Taal',
    'lang.ui_desc': 'Kies de taal voor alle knoppen, labels en navigatie in de software. Dit geldt voor alle apparaten die zijn ingelogd.',
    'lang.content_title': 'Menukaart Vertalingen',
    'lang.content_desc': 'Schakel automatische vertaling in voor productnamen en omschrijvingen op de digitale menukaart. Gasten kunnen dan zelf hun taal kiezen.',
    'lang.content_toggle': 'Menukaart vertalingen inschakelen',
    'lang.content_enabled': 'Ingeschakeld — gasten kunnen de taal kiezen op de digitale menukaart',
    'lang.content_disabled': 'Uitgeschakeld — menukaart wordt alleen in de standaardtaal getoond',
    'lang.translate_btn': 'Vertalingen genereren',
    'lang.translating': 'Vertalen...',
    'lang.translated': 'Vertalingen opgeslagen!',
    'lang.translate_desc': 'Klik om alle productnamen en omschrijvingen automatisch te vertalen met AI. Dit kan even duren.',
    'lang.select_ui': 'Selecteer softwaretaal',
    'lang.enabled_langs': 'Beschikbare talen voor gasten',
    'lang.default_lang': 'Standaardtaal',
    'lang.save_settings': 'Instellingen opslaan',
    // AYCE Menu (guest-facing)
    'ayce.menu': 'Menu',
    'ayce.bestelling': 'Bestelling',
    'ayce.overzicht': 'Overzicht',
    'ayce.rekening': 'Rekening',
    'ayce.service': 'Service',
    'ayce.ronde': 'Ronde',
    'ayce.bestellen': 'Bestellen',
    'ayce.indienen': 'Ronde indienen',
    'ayce.indienen_ellipsis': 'Indienen...',
    'ayce.alles': 'Alles',
    'ayce.populair': 'Populair',
    'ayce.max_een': 'Max. 1',
    'ayce.notitie': 'Notitie',
    'ayce.notitie_placeholder': 'Bijv. geen ui, extra gaar...',
    'ayce.toevoegen': 'Toevoegen',
    'ayce.verwijderen': 'Verwijderen',
    'ayce.cooldown': 'Volgende ronde over',
    'ayce.max_rondes': 'Max. rondes bereikt',
    'ayce.sessie_verlopen': 'Sessie verlopen',
    'ayce.sessie_actief': 'Sessie actief',
    'ayce.tafel': 'Tafel',
    'ayce.gasten': 'gasten',
    'ayce.welkom': 'Welkom!',
    'ayce.geen_sessie': 'Geen actieve sessie',
    'ayce.ongeldige_qr': 'Ongeldige of verlopen QR-code. Vraag een medewerker om hulp.',
    'ayce.service_verzoek': 'Serviceaanvraag',
    'ayce.service_verzonden': 'Verzoek verzonden!',
    'ayce.lege_bestelling': 'Voeg producten toe aan uw bestelling',
    'ayce.bestelling_ingediend': 'Bestelling ingediend!',
    'ayce.rondes_overzicht': 'Rondes overzicht',
    'ayce.geen_rondes': 'Nog geen rondes besteld',
    'ayce.rekening_totaal': 'Totaal',
    'ayce.rekening_per_gast': 'Per gast',
    'ayce.rekening_info': 'De rekening wordt door uw ober gebracht.',
    'ayce.min_producten': 'Minimaal {n} producten vereist',
    'ayce.max_producten': 'Maximum {n} producten bereikt',
    'ayce.dranken_onbeperkt': 'Dranken zijn onbeperkt',
    'ayce.sessie_tijd': 'Sessietijd',
    'ayce.tijd_over': 'over',
    // Menu Preview (guest-facing)
    'menu.menukaart': 'Menukaart',
    'menu.alles': 'Alles',
    'menu.winkelwagen': 'Winkelwagen',
    'menu.bestellingen': 'Bestellingen',
    'menu.service': 'Service',
    'menu.toevoegen': 'Toevoegen',
    'menu.bestellen': 'Bestellen',
    'menu.totaal': 'Totaal',
    'menu.leeg': 'Uw winkelwagen is leeg',
    'menu.populair': 'Populair',
    'menu.aanbevolen': '★ Aanbevolen',
    'menu.opmerking': 'Opmerking (optioneel)',
    'menu.opmerking_placeholder': 'Bijv. zonder ui, extra gaar...',
    'menu.bestelling_plaatsen': 'Bestelling plaatsen',
    'menu.bestelling_geplaatst': 'Bestelling geplaatst!',
    'menu.service_verzoek': 'Serviceaanvraag',
    'menu.service_verzonden': 'Verzoek verzonden!',
    'menu.geen_bestellingen': 'Nog geen bestellingen',
    'menu.status_pending': 'Wachten',
    'menu.status_preparing': 'Wordt bereid',
    'menu.status_ready': 'Klaar',
    'menu.status_served': 'Geserveerd',
    // Service items
    'service.water': 'Water',
    'service.bestek': 'Extra bestek',
    'service.servet': 'Servet',
    'service.rekening': 'Rekening',
    'service.kinderstoel': 'Kinderstoel',
    'service.saus': 'Extra saus',
    'service.ijs': 'Extra ijs',
    'service.stokjes': 'Eetstokjes',
    'service.asbak': 'Asbak',
    // KDS - Kitchen Display System
    'kds.title': 'Keuken Display',
    'kds.subtitle': 'Kitchen Display System',
    'kds.tafel': 'Tafel',
    'kds.ronde': 'Ronde',
    'kds.gasten': 'gasten',
    'kds.couverts': 'couverts',
    'kds.status_new': 'Nieuw',
    'kds.status_in_progress': 'Bezig',
    'kds.status_ready': 'Klaar',
    'kds.status_done': 'Klaar',
    'kds.start': 'Start',
    'kds.totaal_klaar': '✓ Totaal Klaar',
    'kds.klaar': '✓ Klaar',
    'kds.urgent': '⚠ URGENT',
    'kds.terugzetten': 'Terugzetten',
    'kds.teruggezet': 'Teruggezet',
    'kds.items_klaar': 'items klaar',
    'kds.geen_bestellingen': 'Geen bestellingen',
    'kds.geen_actieve': 'Er zijn momenteel geen actieve bestellingen',
    'kds.geen_geschiedenis': 'Geen geschiedenis',
    'kds.afgeronde_hier': 'Afgeronde bestellingen verschijnen hier',
    'kds.wacht_lang': '⚠ Wacht lang',
    'kds.afgehandeld': '✓ Afgehandeld',
    'kds.service': 'Service',
    'kds.rondes': 'Rondes',
    'kds.wacht_bevestiging': 'Wacht op keukenbevestiging',
    'kds.in_bereiding': 'In bereiding',
    'kds.ronde_klaar': '✓ Ronde klaar',
    'kds.ronde_bevestigd': '✓ Ronde bevestigd',
    'kds.wacht_keuken': 'wacht op keuken',
    'kds.bevestigd': 'Bevestigd',
    'kds.geen_ayce_rondes': 'Geen AYCE rondes',
    'kds.ayce_rondes_hier': 'Actieve AYCE rondes verschijnen hier',
    'kds.geen_service': 'Geen service verzoeken',
    'kds.service_hier': 'Verzoeken van gasten verschijnen hier',
    'kds.wachten': 'Wachten',
    'kds.totaal_actief': 'Totaal actief',
    'kds.urgentie': 'Urgentie:',
    'kds.terugzetten_tip': 'Klik op ↺ om een product of bon terug te zetten',
    'kds.geen_restaurant': 'Geen restaurant geselecteerd',
    'kds.volgende_ronde': 'Volgende ronde beschikbaar na',
    'kds.wachttijd': 'min. wachttijd (telt vanaf indiening)',
    'kds.ingediend_om': 'Ingediend om',
    'kds.gedrukt': 'Gedrukt',
    'kds.ayce_keuken': 'AYCE Keuken',
    'kds.route': 'Route',
    'kds.bon_printen': '🖨 Bon Printen',
    'kds.kies_station': 'Kies station voor bon:',
    'kds.print': 'Print',
    'kds.items': 'items',
    'kds.rondes_open': 'Rondes open',
    'kds.items_te_maken': 'Items te maken',
    'kds.groepeer': '📦 Groepeer',
    'kds.groepeer_weergave': 'Groepeer weergave',
    'kds.groepeer_desc': 'Identieke gerechten van alle tafels worden samengevoegd. Maak ze in één keer en markeer alles klaar.',
    'kds.groepeer_desc_kds': 'Alleen gestarte bestellingen worden gegroepeerd — nieuwe orders komen er nooit bij.',
    'kds.groepeer_start_eerst': 'Start eerst een bestelling om te groeperen',
    'kds.bestellingen': 'Bestellingen',
    'kds.niets_groeperen': 'Niets te groeperen',
    'kds.geen_openstaande': 'Geen openstaande rondes',
    'kds.alle_verwerkt': 'Alle rondes zijn verwerkt',
    'kds.geen_orders_station': 'Geen orders voor',
    'kds.station': 'station',
    'kds.klaar_zichtbaar': '✓ Klaar zichtbaar',
    'kds.klaar_verborgen': 'Klaar verborgen',
    'kds.tafels': 'tafels',
    'kds.alles_klaar': '✓ Alles Klaar',
    'kds.bezig': 'Bezig',
    'kds.nieuw': 'Nieuw',
    'kds.alle': 'Alle',
    'kds.geschiedenis': 'Geschiedenis',
    'kds.ayce': 'AYCE',
    'kds.service_verzoek': 'Service',
    'kds.geleden': 'geleden',
    'kds.min': 'min',
    'kds.allergen_warning': 'Allergenen aanwezig',
    'kds.prep_instructions': 'Bereidingsinstructies',
    'kds.order_number': 'Bestelnummer',
  },

  en: {
    'nav.kassa': 'Checkout',
    'nav.beheer': 'Management',
    'nav.promoties_sectie': 'Promotions',
    'nav.kortingscodes': 'Discount Codes',
    'nav.happy_hour': 'Happy Hour',
    'nav.operatie': 'Operations',
    'nav.configuratie': 'Configuration',
    'nav.rapportages': 'Reports',
    'nav.pos_terminal': 'POS Terminal',
    'nav.handheld': 'Handheld',
    'nav.bediening_tablet': 'Service Tablet',
    'nav.keuken_display': 'Kitchen Display',
    'nav.live_keuken_queue': 'Live Kitchen Queue',
    'nav.ayce_keuken': 'AYCE Kitchen',
    'nav.keuken_analytics': 'Kitchen Analytics',
    'nav.bar_display': 'Bar Display',
    'nav.dashboard': 'Dashboard',
    'nav.restaurant_prestaties': 'Restaurant Performance',
    'nav.menukaart': 'Menu',
    'nav.menu_preview': 'Menu Preview',
    'nav.ayce_preview': 'AYCE Preview',
    'nav.all_you_can_eat': 'All You Can Eat',
    'nav.product_database': 'Product Database',
    'nav.vloerplan_editor': 'Floor Plan Editor',
    'nav.zones_tafels': 'Zones & Tables',
    'nav.medewerkers': 'Staff',
    'nav.medewerkersbeheer': 'Staff Admin',
    'nav.promoties': 'Promotions',
    'nav.print_display': 'Print & Display',
    'nav.bestelgeschiedenis': 'Order History',
    'nav.offline_bonnen': 'Offline Receipts',
    'nav.klanten': 'Customers',
    'nav.facturen': 'Invoices',
    'nav.instellingen': 'Settings',
    'nav.testdata': 'Test Data',
    'nav.pre_launch': 'Pre-Launch Validation',
    'nav.data_export': 'Data Export',
    'nav.export_rapporten': 'Export Reports',
    'nav.statistieken': 'Statistics',
    'nav.z_rapport': 'Z-Report',
    'nav.btw_overzicht': 'VAT Overview',
    'sync.synchroniseren': 'Syncing...',
    'sync.wachtend': 'pending',
    'sync.online': 'Online',
    'sync.offline_gesync': 'Offline · Synced',
    'sync.synchronisatie': 'Synchronisation',
    'sync.modus': 'Mode',
    'sync.offline': 'Offline',
    'sync.laatste_sync': 'Last sync',
    'sync.nooit': 'Never',
    'sync.zojuist': 'Just now',
    'sync.geleden': 'ago',
    'sync.wachtende': 'Pending',
    'sync.wijziging': 'change',
    'sync.wijzigingen': 'changes',
    'sync.nu_sync': 'Sync now',
    'sync.inklappen': 'Collapse',
    'sync.uitloggen': 'Log out',
    'common.opslaan': 'Save',
    'common.opslaan_ellipsis': 'Saving...',
    'common.opgeslagen': 'Saved!',
    'common.annuleren': 'Cancel',
    'common.toevoegen': 'Add',
    'common.bewerken': 'Edit',
    'common.verwijderen': 'Delete',
    'common.actief': 'Active',
    'common.inactief': 'Inactive',
    'common.nieuw': 'New',
    'common.zoeken': 'Search',
    'common.laden': 'Loading...',
    'common.sluiten': 'Close',
    'common.bevestigen': 'Confirm',
    'common.ja': 'Yes',
    'common.nee': 'No',
    'common.taal': 'Language',
    'common.talen': 'Languages',
    'settings.bedrijfsprofiel': 'Business Profile',
    'settings.zones': 'Zones',
    'settings.apparaten': 'Devices',
    'settings.categorieen': 'Categories',
    'settings.rollen': 'Staff Roles',
    'settings.ayce': 'AYCE Settings',
    'settings.btw': 'VAT Groups',
    'settings.verbinding': 'Connection & Sync',
    'settings.talen': 'Languages & Translations',
    'lang.title': 'Languages & Translations',
    'lang.subtitle': 'Manage language settings for the software and the menu.',
    'lang.ui_title': 'Software Language',
    'lang.ui_desc': 'Choose the language for all buttons, labels and navigation in the software. This applies to all logged-in devices.',
    'lang.content_title': 'Menu Translations',
    'lang.content_desc': 'Enable automatic translation of product names and descriptions on the digital menu. Guests can then choose their own language.',
    'lang.content_toggle': 'Enable menu translations',
    'lang.content_enabled': 'Enabled — guests can choose their language on the digital menu',
    'lang.content_disabled': 'Disabled — menu is shown in the default language only',
    'lang.translate_btn': 'Generate translations',
    'lang.translating': 'Translating...',
    'lang.translated': 'Translations saved!',
    'lang.translate_desc': 'Click to automatically translate all product names and descriptions using AI. This may take a moment.',
    'lang.select_ui': 'Select software language',
    'lang.enabled_langs': 'Available languages for guests',
    'lang.default_lang': 'Default language',
    'lang.save_settings': 'Save settings',
    'ayce.menu': 'Menu',
    'ayce.bestelling': 'Order',
    'ayce.overzicht': 'Overview',
    'ayce.rekening': 'Bill',
    'ayce.service': 'Service',
    'ayce.ronde': 'Round',
    'ayce.bestellen': 'Order',
    'ayce.indienen': 'Submit round',
    'ayce.indienen_ellipsis': 'Submitting...',
    'ayce.alles': 'All',
    'ayce.populair': 'Popular',
    'ayce.max_een': 'Max. 1',
    'ayce.notitie': 'Note',
    'ayce.notitie_placeholder': 'E.g. no onion, well done...',
    'ayce.toevoegen': 'Add',
    'ayce.verwijderen': 'Remove',
    'ayce.cooldown': 'Next round in',
    'ayce.max_rondes': 'Max rounds reached',
    'ayce.sessie_verlopen': 'Session expired',
    'ayce.sessie_actief': 'Session active',
    'ayce.tafel': 'Table',
    'ayce.gasten': 'guests',
    'ayce.welkom': 'Welcome!',
    'ayce.geen_sessie': 'No active session',
    'ayce.ongeldige_qr': 'Invalid or expired QR code. Please ask a staff member for help.',
    'ayce.service_verzoek': 'Service request',
    'ayce.service_verzonden': 'Request sent!',
    'ayce.lege_bestelling': 'Add products to your order',
    'ayce.bestelling_ingediend': 'Order submitted!',
    'ayce.rondes_overzicht': 'Rounds overview',
    'ayce.geen_rondes': 'No rounds ordered yet',
    'ayce.rekening_totaal': 'Total',
    'ayce.rekening_per_gast': 'Per guest',
    'ayce.rekening_info': 'Your bill will be brought by your waiter.',
    'ayce.min_producten': 'Minimum {n} products required',
    'ayce.max_producten': 'Maximum {n} products reached',
    'ayce.dranken_onbeperkt': 'Drinks are unlimited',
    'ayce.sessie_tijd': 'Session time',
    'ayce.tijd_over': 'remaining',
    // Menu Preview (guest-facing)
    'menu.menukaart': 'Menu',
    'menu.alles': 'All',
    'menu.winkelwagen': 'Cart',
    'menu.bestellingen': 'Orders',
    'menu.service': 'Service',
    'menu.toevoegen': 'Add',
    'menu.bestellen': 'Order',
    'menu.totaal': 'Total',
    'menu.leeg': 'Your cart is empty',
    'menu.populair': 'Popular',
    'menu.aanbevolen': '★ Recommended',
    'menu.opmerking': 'Note (optional)',
    'menu.opmerking_placeholder': 'E.g. no onion, well done...',
    'menu.bestelling_plaatsen': 'Place order',
    'menu.bestelling_geplaatst': 'Order placed!',
    'menu.service_verzoek': 'Service request',
    'menu.service_verzonden': 'Request sent!',
    'menu.geen_bestellingen': 'No orders yet',
    'menu.status_pending': 'Waiting',
    'menu.status_preparing': 'Preparing',
    'menu.status_ready': 'Ready',
    'menu.status_served': 'Served',
    // Service items
    'service.water': 'Water',
    'service.bestek': 'Extra cutlery',
    'service.servet': 'Napkin',
    'service.rekening': 'Bill',
    'service.kinderstoel': 'High chair',
    'service.saus': 'Extra sauce',
    'service.ijs': 'Extra ice',
    'service.stokjes': 'Chopsticks',
    'service.asbak': 'Ashtray',
    // KDS - Kitchen Display System
    'kds.title': 'Kitchen Display',
    'kds.subtitle': 'Kitchen Display System',
    'kds.tafel': 'Table',
    'kds.ronde': 'Round',
    'kds.gasten': 'guests',
    'kds.couverts': 'covers',
    'kds.status_new': 'New',
    'kds.status_in_progress': 'In Progress',
    'kds.status_ready': 'Ready',
    'kds.status_done': 'Done',
    'kds.start': 'Start',
    'kds.totaal_klaar': '✓ All Done',
    'kds.klaar': '✓ Done',
    'kds.urgent': '⚠ URGENT',
    'kds.terugzetten': 'Restore',
    'kds.teruggezet': 'Restored',
    'kds.items_klaar': 'items done',
    'kds.geen_bestellingen': 'No orders',
    'kds.geen_actieve': 'There are currently no active orders',
    'kds.geen_geschiedenis': 'No history',
    'kds.afgeronde_hier': 'Completed orders appear here',
    'kds.wacht_lang': '⚠ Waiting long',
    'kds.afgehandeld': '✓ Handled',
    'kds.service': 'Service',
    'kds.rondes': 'Rounds',
    'kds.wacht_bevestiging': 'Awaiting kitchen confirmation',
    'kds.in_bereiding': 'In preparation',
    'kds.ronde_klaar': '✓ Round done',
    'kds.ronde_bevestigd': '✓ Round confirmed',
    'kds.wacht_keuken': 'awaiting kitchen',
    'kds.bevestigd': 'Confirmed',
    'kds.geen_ayce_rondes': 'No AYCE rounds',
    'kds.ayce_rondes_hier': 'Active AYCE rounds appear here',
    'kds.geen_service': 'No service requests',
    'kds.service_hier': 'Guest requests appear here',
    'kds.wachten': 'Waiting',
    'kds.totaal_actief': 'Total active',
    'kds.urgentie': 'Urgency:',
    'kds.terugzetten_tip': 'Click ↺ to restore a product or ticket',
    'kds.geen_restaurant': 'No restaurant selected',
    'kds.volgende_ronde': 'Next round available after',
    'kds.wachttijd': 'min. wait time (from submission)',
    'kds.ingediend_om': 'Submitted at',
    'kds.gedrukt': 'Printed',
    'kds.ayce_keuken': 'AYCE Kitchen',
    'kds.route': 'Route',
    'kds.bon_printen': '🖨 Print Ticket',
    'kds.kies_station': 'Choose station for ticket:',
    'kds.print': 'Print',
    'kds.items': 'items',
    'kds.rondes_open': 'Open rounds',
    'kds.items_te_maken': 'Items to make',
    'kds.groepeer': '📦 Group',
    'kds.groepeer_weergave': 'Grouped view',
    'kds.groepeer_desc': 'Identical dishes from all tables are combined. Make them at once and mark all done.',
    'kds.groepeer_desc_kds': 'Only started orders are grouped — new orders never join an active group.',
    'kds.groepeer_start_eerst': 'Start an order first to group items',
    'kds.bestellingen': 'Orders',
    'kds.niets_groeperen': 'Nothing to group',
    'kds.geen_openstaande': 'No open rounds',
    'kds.alle_verwerkt': 'All rounds processed',
    'kds.geen_orders_station': 'No orders for',
    'kds.station': 'station',
    'kds.klaar_zichtbaar': '✓ Done visible',
    'kds.klaar_verborgen': 'Done hidden',
    'kds.tafels': 'tables',
    'kds.alles_klaar': '✓ All Done',
    'kds.bezig': 'In Progress',
    'kds.nieuw': 'New',
    'kds.alle': 'All',
    'kds.geschiedenis': 'History',
    'kds.ayce': 'AYCE',
    'kds.service_verzoek': 'Service',
    'kds.geleden': 'ago',
    'kds.min': 'min',
    'kds.allergen_warning': 'Allergens present',
    'kds.prep_instructions': 'Prep instructions',
    'kds.order_number': 'Order number',
  },

  fr: {
    'nav.kassa': 'Caisse',
    'nav.beheer': 'Gestion',
    'nav.promoties_sectie': 'Promotions',
    'nav.kortingscodes': 'Codes de Réduction',
    'nav.happy_hour': 'Happy Hour',
    'nav.operatie': 'Opérations',
    'nav.configuratie': 'Configuration',
    'nav.rapportages': 'Rapports',
    'nav.pos_terminal': 'Terminal POS',
    'nav.handheld': 'Portable',
    'nav.bediening_tablet': 'Tablette Service',
    'nav.keuken_display': 'Affichage Cuisine',
    'nav.live_keuken_queue': 'File Cuisine Live',
    'nav.ayce_keuken': 'Cuisine AYCE',
    'nav.keuken_analytics': 'Analytique Cuisine',
    'nav.bar_display': 'Affichage Bar',
    'nav.dashboard': 'Tableau de bord',
    'nav.restaurant_prestaties': 'Performance Restaurant',
    'nav.menukaart': 'Menu',
    'nav.menu_preview': 'Aperçu Menu',
    'nav.ayce_preview': 'Aperçu AYCE',
    'nav.all_you_can_eat': 'À Volonté',
    'nav.product_database': 'Base de Produits',
    'nav.vloerplan_editor': 'Éditeur de Plan',
    'nav.zones_tafels': 'Zones & Tables',
    'nav.medewerkers': 'Personnel',
    'nav.medewerkersbeheer': 'Admin Personnel',
    'nav.promoties': 'Promotions',
    'nav.print_display': 'Impression & Affichage',
    'nav.bestelgeschiedenis': 'Historique Commandes',
    'nav.offline_bonnen': 'Reçus Hors Ligne',
    'nav.klanten': 'Clients',
    'nav.facturen': 'Factures',
    'nav.instellingen': 'Paramètres',
    'nav.testdata': 'Données Test',
    'nav.pre_launch': 'Validation Pré-Lancement',
    'nav.data_export': 'Export Données',
    'nav.export_rapporten': 'Export Rapports',
    'nav.statistieken': 'Statistiques',
    'nav.z_rapport': 'Rapport Z',
    'nav.btw_overzicht': 'Aperçu TVA',
    'sync.synchroniseren': 'Synchronisation...',
    'sync.wachtend': 'en attente',
    'sync.online': 'En ligne',
    'sync.offline_gesync': 'Hors ligne · Synchronisé',
    'sync.synchronisatie': 'Synchronisation',
    'sync.modus': 'Mode',
    'sync.offline': 'Hors ligne',
    'sync.laatste_sync': 'Dernière sync',
    'sync.nooit': 'Jamais',
    'sync.zojuist': 'À l\'instant',
    'sync.geleden': 'il y a',
    'sync.wachtende': 'En attente',
    'sync.wijziging': 'modification',
    'sync.wijzigingen': 'modifications',
    'sync.nu_sync': 'Sync maintenant',
    'sync.inklappen': 'Réduire',
    'sync.uitloggen': 'Déconnexion',
    'common.opslaan': 'Enregistrer',
    'common.opslaan_ellipsis': 'Enregistrement...',
    'common.opgeslagen': 'Enregistré!',
    'common.annuleren': 'Annuler',
    'common.toevoegen': 'Ajouter',
    'common.bewerken': 'Modifier',
    'common.verwijderen': 'Supprimer',
    'common.actief': 'Actif',
    'common.inactief': 'Inactif',
    'common.nieuw': 'Nouveau',
    'common.zoeken': 'Rechercher',
    'common.laden': 'Chargement...',
    'common.sluiten': 'Fermer',
    'common.bevestigen': 'Confirmer',
    'common.ja': 'Oui',
    'common.nee': 'Non',
    'common.taal': 'Langue',
    'common.talen': 'Langues',
    'settings.bedrijfsprofiel': 'Profil d\'entreprise',
    'settings.zones': 'Zones',
    'settings.apparaten': 'Appareils',
    'settings.categorieen': 'Catégories',
    'settings.rollen': 'Rôles du Personnel',
    'settings.ayce': 'Paramètres AYCE',
    'settings.btw': 'Groupes TVA',
    'settings.verbinding': 'Connexion & Sync',
    'settings.talen': 'Langues & Traductions',
    'lang.title': 'Langues & Traductions',
    'lang.subtitle': 'Gérez les paramètres de langue pour le logiciel et le menu.',
    'lang.ui_title': 'Langue du Logiciel',
    'lang.ui_desc': 'Choisissez la langue pour tous les boutons, étiquettes et la navigation dans le logiciel.',
    'lang.content_title': 'Traductions du Menu',
    'lang.content_desc': 'Activez la traduction automatique des noms et descriptions de produits sur le menu numérique.',
    'lang.content_toggle': 'Activer les traductions du menu',
    'lang.content_enabled': 'Activé — les clients peuvent choisir leur langue sur le menu numérique',
    'lang.content_disabled': 'Désactivé — le menu est affiché uniquement dans la langue par défaut',
    'lang.translate_btn': 'Générer les traductions',
    'lang.translating': 'Traduction...',
    'lang.translated': 'Traductions enregistrées!',
    'lang.translate_desc': 'Cliquez pour traduire automatiquement tous les noms et descriptions de produits avec l\'IA.',
    'lang.select_ui': 'Sélectionner la langue du logiciel',
    'lang.enabled_langs': 'Langues disponibles pour les clients',
    'lang.default_lang': 'Langue par défaut',
    'lang.save_settings': 'Enregistrer les paramètres',
    'ayce.menu': 'Menu',
    'ayce.bestelling': 'Commande',
    'ayce.overzicht': 'Aperçu',
    'ayce.rekening': 'Addition',
    'ayce.service': 'Service',
    'ayce.ronde': 'Tour',
    'ayce.bestellen': 'Commander',
    'ayce.indienen': 'Soumettre le tour',
    'ayce.indienen_ellipsis': 'Envoi...',
    'ayce.alles': 'Tout',
    'ayce.populair': 'Populaire',
    'ayce.max_een': 'Max. 1',
    'ayce.notitie': 'Note',
    'ayce.notitie_placeholder': 'Ex. sans oignon, bien cuit...',
    'ayce.toevoegen': 'Ajouter',
    'ayce.verwijderen': 'Supprimer',
    'ayce.cooldown': 'Prochain tour dans',
    'ayce.max_rondes': 'Tours maximum atteints',
    'ayce.sessie_verlopen': 'Session expirée',
    'ayce.sessie_actief': 'Session active',
    'ayce.tafel': 'Table',
    'ayce.gasten': 'convives',
    'ayce.welkom': 'Bienvenue!',
    'ayce.geen_sessie': 'Aucune session active',
    'ayce.ongeldige_qr': 'QR code invalide ou expiré. Veuillez demander l\'aide d\'un membre du personnel.',
    'ayce.service_verzoek': 'Demande de service',
    'ayce.service_verzonden': 'Demande envoyée!',
    'ayce.lege_bestelling': 'Ajoutez des produits à votre commande',
    'ayce.bestelling_ingediend': 'Commande soumise!',
    'ayce.rondes_overzicht': 'Aperçu des tours',
    'ayce.geen_rondes': 'Aucun tour commandé',
    'ayce.rekening_totaal': 'Total',
    'ayce.rekening_per_gast': 'Par convive',
    'ayce.rekening_info': 'L\'addition vous sera apportée par votre serveur.',
    'ayce.min_producten': 'Minimum {n} produits requis',
    'ayce.max_producten': 'Maximum {n} produits atteint',
    'ayce.dranken_onbeperkt': 'Les boissons sont illimitées',
    'ayce.sessie_tijd': 'Durée de session',
    'ayce.tijd_over': 'restant',
    // Menu Preview (guest-facing)
    'menu.menukaart': 'Menu',
    'menu.alles': 'Tout',
    'menu.winkelwagen': 'Panier',
    'menu.bestellingen': 'Commandes',
    'menu.service': 'Service',
    'menu.toevoegen': 'Ajouter',
    'menu.bestellen': 'Commander',
    'menu.totaal': 'Total',
    'menu.leeg': 'Votre panier est vide',
    'menu.populair': 'Populaire',
    'menu.aanbevolen': '★ Recommandé',
    'menu.opmerking': 'Remarque (optionnel)',
    'menu.opmerking_placeholder': 'Ex. sans oignon, bien cuit...',
    'menu.bestelling_plaatsen': 'Passer la commande',
    'menu.bestelling_geplaatst': 'Commande passée!',
    'menu.service_verzoek': 'Demande de service',
    'menu.service_verzonden': 'Demande envoyée!',
    'menu.geen_bestellingen': 'Aucune commande',
    'menu.status_pending': 'En attente',
    'menu.status_preparing': 'En préparation',
    'menu.status_ready': 'Prêt',
    'menu.status_served': 'Servi',
    // Service items
    'service.water': 'Eau',
    'service.bestek': 'Couverts supplémentaires',
    'service.servet': 'Serviette',
    'service.rekening': 'Addition',
    'service.kinderstoel': 'Chaise haute',
    'service.saus': 'Sauce supplémentaire',
    'service.ijs': 'Glaçons supplémentaires',
    'service.stokjes': 'Baguettes',
    'service.asbak': 'Cendrier',
    // KDS - Kitchen Display System
    'kds.title': 'Affichage Cuisine',
    'kds.subtitle': 'Système d\'Affichage Cuisine',
    'kds.tafel': 'Table',
    'kds.ronde': 'Tour',
    'kds.gasten': 'convives',
    'kds.couverts': 'couverts',
    'kds.status_new': 'Nouveau',
    'kds.status_in_progress': 'En cours',
    'kds.status_ready': 'Prêt',
    'kds.status_done': 'Terminé',
    'kds.start': 'Démarrer',
    'kds.totaal_klaar': '✓ Tout Prêt',
    'kds.klaar': '✓ Prêt',
    'kds.urgent': '⚠ URGENT',
    'kds.terugzetten': 'Restaurer',
    'kds.teruggezet': 'Restauré',
    'kds.items_klaar': 'articles prêts',
    'kds.geen_bestellingen': 'Aucune commande',
    'kds.geen_actieve': 'Il n\'y a actuellement aucune commande active',
    'kds.geen_geschiedenis': 'Aucun historique',
    'kds.afgeronde_hier': 'Les commandes terminées apparaissent ici',
    'kds.wacht_lang': '⚠ Attente longue',
    'kds.afgehandeld': '✓ Traité',
    'kds.service': 'Service',
    'kds.rondes': 'Tours',
    'kds.wacht_bevestiging': 'En attente de confirmation cuisine',
    'kds.in_bereiding': 'En préparation',
    'kds.ronde_klaar': '✓ Tour terminé',
    'kds.ronde_bevestigd': '✓ Tour confirmé',
    'kds.wacht_keuken': 'en attente cuisine',
    'kds.bevestigd': 'Confirmé',
    'kds.geen_ayce_rondes': 'Aucun tour AYCE',
    'kds.ayce_rondes_hier': 'Les tours AYCE actifs apparaissent ici',
    'kds.geen_service': 'Aucune demande de service',
    'kds.service_hier': 'Les demandes des clients apparaissent ici',
    'kds.wachten': 'En attente',
    'kds.totaal_actief': 'Total actif',
    'kds.urgentie': 'Urgence:',
    'kds.terugzetten_tip': 'Cliquez sur ↺ pour restaurer un produit ou un ticket',
    'kds.geen_restaurant': 'Aucun restaurant sélectionné',
    'kds.volgende_ronde': 'Prochain tour disponible après',
    'kds.wachttijd': 'min. d\'attente (depuis la soumission)',
    'kds.ingediend_om': 'Soumis à',
    'kds.gedrukt': 'Imprimé',
    'kds.ayce_keuken': 'Cuisine AYCE',
    'kds.route': 'Itinéraire',
    'kds.bon_printen': '🖨 Imprimer Ticket',
    'kds.kies_station': 'Choisir le poste pour le ticket:',
    'kds.print': 'Imprimer',
    'kds.items': 'articles',
    'kds.rondes_open': 'Tours ouverts',
    'kds.items_te_maken': 'Articles à préparer',
    'kds.groepeer': '📦 Grouper',
    'kds.groepeer_weergave': 'Vue groupée',
    'kds.groepeer_desc': 'Les plats identiques de toutes les tables sont regroupés. Préparez-les en une fois et marquez tout comme prêt.',
    'kds.groepeer_desc_kds': 'Seules les commandes démarrées sont regroupées — les nouvelles commandes ne rejoignent jamais un groupe actif.',
    'kds.groepeer_start_eerst': 'Démarrez d\'abord une commande pour regrouper',
    'kds.bestellingen': 'Commandes',
    'kds.niets_groeperen': 'Rien à grouper',
    'kds.geen_openstaande': 'Aucun tour en attente',
    'kds.alle_verwerkt': 'Tous les tours sont traités',
    'kds.geen_orders_station': 'Aucune commande pour',
    'kds.station': 'poste',
    'kds.klaar_zichtbaar': '✓ Terminé visible',
    'kds.klaar_verborgen': 'Terminé masqué',
    'kds.tafels': 'tables',
    'kds.alles_klaar': '✓ Tout Prêt',
    'kds.bezig': 'En cours',
    'kds.nieuw': 'Nouveau',
    'kds.alle': 'Tout',
    'kds.geschiedenis': 'Historique',
    'kds.ayce': 'AYCE',
    'kds.service_verzoek': 'Service',
    'kds.geleden': 'il y a',
    'kds.min': 'min',
    'kds.allergen_warning': 'Allergènes présents',
    'kds.prep_instructions': 'Instructions de préparation',
    'kds.order_number': 'Numéro de commande',
  },

  es: {
    'nav.kassa': 'Caja',
    'nav.beheer': 'Gestión',
    'nav.promoties_sectie': 'Promociones',
    'nav.kortingscodes': 'Códigos de Descuento',
    'nav.happy_hour': 'Happy Hour',
    'nav.operatie': 'Operaciones',
    'nav.configuratie': 'Configuración',
    'nav.rapportages': 'Informes',
    'nav.pos_terminal': 'Terminal POS',
    'nav.handheld': 'Portátil',
    'nav.bediening_tablet': 'Tablet Servicio',
    'nav.keuken_display': 'Pantalla Cocina',
    'nav.live_keuken_queue': 'Cola Cocina en Vivo',
    'nav.ayce_keuken': 'Cocina AYCE',
    'nav.keuken_analytics': 'Analítica Cocina',
    'nav.bar_display': 'Pantalla Bar',
    'nav.dashboard': 'Panel',
    'nav.restaurant_prestaties': 'Rendimiento Restaurante',
    'nav.menukaart': 'Carta',
    'nav.menu_preview': 'Vista Previa Carta',
    'nav.ayce_preview': 'Vista Previa AYCE',
    'nav.all_you_can_eat': 'Todo lo que Puedas Comer',
    'nav.product_database': 'Base de Productos',
    'nav.vloerplan_editor': 'Editor de Plano',
    'nav.zones_tafels': 'Zonas & Mesas',
    'nav.medewerkers': 'Personal',
    'nav.medewerkersbeheer': 'Admin Personal',
    'nav.promoties': 'Promociones',
    'nav.print_display': 'Impresión & Pantalla',
    'nav.bestelgeschiedenis': 'Historial Pedidos',
    'nav.offline_bonnen': 'Recibos Sin Conexión',
    'nav.klanten': 'Clientes',
    'nav.facturen': 'Facturas',
    'nav.instellingen': 'Ajustes',
    'nav.testdata': 'Datos de Prueba',
    'nav.pre_launch': 'Validación Pre-Lanzamiento',
    'nav.data_export': 'Exportar Datos',
    'nav.export_rapporten': 'Exportar Informes',
    'nav.statistieken': 'Estadísticas',
    'nav.z_rapport': 'Informe Z',
    'nav.btw_overzicht': 'Resumen IVA',
    'sync.synchroniseren': 'Sincronizando...',
    'sync.wachtend': 'pendiente',
    'sync.online': 'En línea',
    'sync.offline_gesync': 'Sin conexión · Sincronizado',
    'sync.synchronisatie': 'Sincronización',
    'sync.modus': 'Modo',
    'sync.offline': 'Sin conexión',
    'sync.laatste_sync': 'Última sync',
    'sync.nooit': 'Nunca',
    'sync.zojuist': 'Ahora mismo',
    'sync.geleden': 'hace',
    'sync.wachtende': 'Pendiente',
    'sync.wijziging': 'cambio',
    'sync.wijzigingen': 'cambios',
    'sync.nu_sync': 'Sync ahora',
    'sync.inklappen': 'Colapsar',
    'sync.uitloggen': 'Cerrar sesión',
    'common.opslaan': 'Guardar',
    'common.opslaan_ellipsis': 'Guardando...',
    'common.opgeslagen': '¡Guardado!',
    'common.annuleren': 'Cancelar',
    'common.toevoegen': 'Añadir',
    'common.bewerken': 'Editar',
    'common.verwijderen': 'Eliminar',
    'common.actief': 'Activo',
    'common.inactief': 'Inactivo',
    'common.nieuw': 'Nuevo',
    'common.zoeken': 'Buscar',
    'common.laden': 'Cargando...',
    'common.sluiten': 'Cerrar',
    'common.bevestigen': 'Confirmar',
    'common.ja': 'Sí',
    'common.nee': 'No',
    'common.taal': 'Idioma',
    'common.talen': 'Idiomas',
    'settings.bedrijfsprofiel': 'Perfil de Empresa',
    'settings.zones': 'Zonas',
    'settings.apparaten': 'Dispositivos',
    'settings.categorieen': 'Categorías',
    'settings.rollen': 'Roles del Personal',
    'settings.ayce': 'Ajustes AYCE',
    'settings.btw': 'Grupos IVA',
    'settings.verbinding': 'Conexión & Sync',
    'settings.talen': 'Idiomas & Traducciones',
    'lang.title': 'Idiomas & Traducciones',
    'lang.subtitle': 'Gestione los ajustes de idioma para el software y la carta.',
    'lang.ui_title': 'Idioma del Software',
    'lang.ui_desc': 'Elija el idioma para todos los botones, etiquetas y navegación en el software.',
    'lang.content_title': 'Traducciones de la Carta',
    'lang.content_desc': 'Active la traducción automática de nombres y descripciones de productos en la carta digital.',
    'lang.content_toggle': 'Activar traducciones de la carta',
    'lang.content_enabled': 'Activado — los clientes pueden elegir su idioma en la carta digital',
    'lang.content_disabled': 'Desactivado — la carta se muestra solo en el idioma predeterminado',
    'lang.translate_btn': 'Generar traducciones',
    'lang.translating': 'Traduciendo...',
    'lang.translated': '¡Traducciones guardadas!',
    'lang.translate_desc': 'Haga clic para traducir automáticamente todos los nombres y descripciones de productos con IA.',
    'lang.select_ui': 'Seleccionar idioma del software',
    'lang.enabled_langs': 'Idiomas disponibles para clientes',
    'lang.default_lang': 'Idioma predeterminado',
    'lang.save_settings': 'Guardar ajustes',
    'ayce.menu': 'Carta',
    'ayce.bestelling': 'Pedido',
    'ayce.overzicht': 'Resumen',
    'ayce.rekening': 'Cuenta',
    'ayce.service': 'Servicio',
    'ayce.ronde': 'Ronda',
    'ayce.bestellen': 'Pedir',
    'ayce.indienen': 'Enviar ronda',
    'ayce.indienen_ellipsis': 'Enviando...',
    'ayce.alles': 'Todo',
    'ayce.populair': 'Popular',
    'ayce.max_een': 'Máx. 1',
    'ayce.notitie': 'Nota',
    'ayce.notitie_placeholder': 'Ej. sin cebolla, muy hecho...',
    'ayce.toevoegen': 'Añadir',
    'ayce.verwijderen': 'Quitar',
    'ayce.cooldown': 'Próxima ronda en',
    'ayce.max_rondes': 'Rondas máximas alcanzadas',
    'ayce.sessie_verlopen': 'Sesión expirada',
    'ayce.sessie_actief': 'Sesión activa',
    'ayce.tafel': 'Mesa',
    'ayce.gasten': 'comensales',
    'ayce.welkom': '¡Bienvenido!',
    'ayce.geen_sessie': 'Sin sesión activa',
    'ayce.ongeldige_qr': 'Código QR inválido o expirado. Por favor pida ayuda a un miembro del personal.',
    'ayce.service_verzoek': 'Solicitud de servicio',
    'ayce.service_verzonden': '¡Solicitud enviada!',
    'ayce.lege_bestelling': 'Añada productos a su pedido',
    'ayce.bestelling_ingediend': '¡Pedido enviado!',
    'ayce.rondes_overzicht': 'Resumen de rondas',
    'ayce.geen_rondes': 'Aún no hay rondas pedidas',
    'ayce.rekening_totaal': 'Total',
    'ayce.rekening_per_gast': 'Por comensal',
    'ayce.rekening_info': 'Su cuenta será traída por su camarero.',
    'ayce.min_producten': 'Mínimo {n} productos requeridos',
    'ayce.max_producten': 'Máximo {n} productos alcanzado',
    'ayce.dranken_onbeperkt': 'Las bebidas son ilimitadas',
    'ayce.sessie_tijd': 'Tiempo de sesión',
    'ayce.tijd_over': 'restante',
    // Menu Preview (guest-facing)
    'menu.menukaart': 'Carta',
    'menu.alles': 'Todo',
    'menu.winkelwagen': 'Carrito',
    'menu.bestellingen': 'Pedidos',
    'menu.service': 'Servicio',
    'menu.toevoegen': 'Añadir',
    'menu.bestellen': 'Pedir',
    'menu.totaal': 'Total',
    'menu.leeg': 'Su carrito está vacío',
    'menu.populair': 'Popular',
    'menu.aanbevolen': '★ Recomendado',
    'menu.opmerking': 'Nota (opcional)',
    'menu.opmerking_placeholder': 'Ej. sin cebolla, muy hecho...',
    'menu.bestelling_plaatsen': 'Realizar pedido',
    'menu.bestelling_geplaatst': '¡Pedido realizado!',
    'menu.service_verzoek': 'Solicitud de servicio',
    'menu.service_verzonden': '¡Solicitud enviada!',
    'menu.geen_bestellingen': 'Sin pedidos aún',
    'menu.status_pending': 'Esperando',
    'menu.status_preparing': 'Preparando',
    'menu.status_ready': 'Listo',
    'menu.status_served': 'Servido',
    // Service items
    'service.water': 'Agua',
    'service.bestek': 'Cubiertos extra',
    'service.servet': 'Servilleta',
    'service.rekening': 'Cuenta',
    'service.kinderstoel': 'Trona',
    'service.saus': 'Salsa extra',
    'service.ijs': 'Hielo extra',
    'service.stokjes': 'Palillos',
    'service.asbak': 'Cenicero',
    // KDS - Kitchen Display System
    'kds.title': 'Pantalla Cocina',
    'kds.subtitle': 'Sistema de Pantalla de Cocina',
    'kds.tafel': 'Mesa',
    'kds.ronde': 'Ronda',
    'kds.gasten': 'comensales',
    'kds.couverts': 'cubiertos',
    'kds.status_new': 'Nuevo',
    'kds.status_in_progress': 'En proceso',
    'kds.status_ready': 'Listo',
    'kds.status_done': 'Hecho',
    'kds.start': 'Iniciar',
    'kds.totaal_klaar': '✓ Todo Listo',
    'kds.klaar': '✓ Listo',
    'kds.urgent': '⚠ URGENTE',
    'kds.terugzetten': 'Restaurar',
    'kds.teruggezet': 'Restaurado',
    'kds.items_klaar': 'artículos listos',
    'kds.geen_bestellingen': 'Sin pedidos',
    'kds.geen_actieve': 'Actualmente no hay pedidos activos',
    'kds.geen_geschiedenis': 'Sin historial',
    'kds.afgeronde_hier': 'Los pedidos completados aparecen aquí',
    'kds.wacht_lang': '⚠ Espera larga',
    'kds.afgehandeld': '✓ Atendido',
    'kds.service': 'Servicio',
    'kds.rondes': 'Rondas',
    'kds.wacht_bevestiging': 'Esperando confirmación de cocina',
    'kds.in_bereiding': 'En preparación',
    'kds.ronde_klaar': '✓ Ronda lista',
    'kds.ronde_bevestigd': '✓ Ronda confirmada',
    'kds.wacht_keuken': 'esperando cocina',
    'kds.bevestigd': 'Confirmado',
    'kds.geen_ayce_rondes': 'Sin rondas AYCE',
    'kds.ayce_rondes_hier': 'Las rondas AYCE activas aparecen aquí',
    'kds.geen_service': 'Sin solicitudes de servicio',
    'kds.service_hier': 'Las solicitudes de los clientes aparecen aquí',
    'kds.wachten': 'Esperando',
    'kds.totaal_actief': 'Total activo',
    'kds.urgentie': 'Urgencia:',
    'kds.terugzetten_tip': 'Haga clic en ↺ para restaurar un producto o ticket',
    'kds.geen_restaurant': 'Sin restaurante seleccionado',
    'kds.volgende_ronde': 'Próxima ronda disponible después de',
    'kds.wachttijd': 'min. de espera (desde el envío)',
    'kds.ingediend_om': 'Enviado a las',
    'kds.gedrukt': 'Impreso',
    'kds.ayce_keuken': 'Cocina AYCE',
    'kds.route': 'Ruta',
    'kds.bon_printen': '🖨 Imprimir Ticket',
    'kds.kies_station': 'Elegir estación para el ticket:',
    'kds.print': 'Imprimir',
    'kds.items': 'artículos',
    'kds.rondes_open': 'Rondas abiertas',
    'kds.items_te_maken': 'Artículos a preparar',
    'kds.groepeer': '📦 Agrupar',
    'kds.groepeer_weergave': 'Vista agrupada',
    'kds.groepeer_desc': 'Los platos idénticos de todas las mesas se combinan. Prepárelos de una vez y marque todo como listo.',
    'kds.groepeer_desc_kds': 'Solo se agrupan los pedidos iniciados — los nuevos pedidos nunca se unen a un grupo activo.',
    'kds.groepeer_start_eerst': 'Inicie primero un pedido para agrupar',
    'kds.bestellingen': 'Pedidos',
    'kds.niets_groeperen': 'Nada que agrupar',
    'kds.geen_openstaande': 'Sin rondas pendientes',
    'kds.alle_verwerkt': 'Todas las rondas procesadas',
    'kds.geen_orders_station': 'Sin pedidos para',
    'kds.station': 'estación',
    'kds.klaar_zichtbaar': '✓ Listo visible',
    'kds.klaar_verborgen': 'Listo oculto',
    'kds.tafels': 'mesas',
    'kds.alles_klaar': '✓ Todo Listo',
    'kds.bezig': 'En proceso',
    'kds.nieuw': 'Nuevo',
    'kds.alle': 'Todo',
    'kds.geschiedenis': 'Historial',
    'kds.ayce': 'AYCE',
    'kds.service_verzoek': 'Servicio',
    'kds.geleden': 'hace',
    'kds.min': 'min',
    'kds.allergen_warning': 'Alérgenos presentes',
    'kds.prep_instructions': 'Instrucciones de preparación',
    'kds.order_number': 'Número de pedido',
  },

  it: {
    'nav.kassa': 'Cassa',
    'nav.beheer': 'Gestione',
    'nav.promoties_sectie': 'Promozioni',
    'nav.kortingscodes': 'Codici Sconto',
    'nav.happy_hour': 'Happy Hour',
    'nav.operatie': 'Operazioni',
    'nav.configuratie': 'Configurazione',
    'nav.rapportages': 'Rapporti',
    'nav.pos_terminal': 'Terminale POS',
    'nav.handheld': 'Palmare',
    'nav.bediening_tablet': 'Tablet Servizio',
    'nav.keuken_display': 'Display Cucina',
    'nav.live_keuken_queue': 'Coda Cucina Live',
    'nav.ayce_keuken': 'Cucina AYCE',
    'nav.keuken_analytics': 'Analisi Cucina',
    'nav.bar_display': 'Display Bar',
    'nav.dashboard': 'Pannello',
    'nav.restaurant_prestaties': 'Prestazioni Ristorante',
    'nav.menukaart': 'Menu',
    'nav.menu_preview': 'Anteprima Menu',
    'nav.ayce_preview': 'Anteprima AYCE',
    'nav.all_you_can_eat': 'All You Can Eat',
    'nav.product_database': 'Database Prodotti',
    'nav.vloerplan_editor': 'Editor Planimetria',
    'nav.zones_tafels': 'Zone & Tavoli',
    'nav.medewerkers': 'Personale',
    'nav.medewerkersbeheer': 'Admin Personale',
    'nav.promoties': 'Promozioni',
    'nav.print_display': 'Stampa & Display',
    'nav.bestelgeschiedenis': 'Storico Ordini',
    'nav.offline_bonnen': 'Ricevute Offline',
    'nav.klanten': 'Clienti',
    'nav.facturen': 'Fatture',
    'nav.instellingen': 'Impostazioni',
    'nav.testdata': 'Dati di Test',
    'nav.pre_launch': 'Validazione Pre-Lancio',
    'nav.data_export': 'Esporta Dati',
    'nav.export_rapporten': 'Esporta Rapporti',
    'nav.statistieken': 'Statistiche',
    'nav.z_rapport': 'Rapporto Z',
    'nav.btw_overzicht': 'Panoramica IVA',
    'sync.synchroniseren': 'Sincronizzazione...',
    'sync.wachtend': 'in attesa',
    'sync.online': 'Online',
    'sync.offline_gesync': 'Offline · Sincronizzato',
    'sync.synchronisatie': 'Sincronizzazione',
    'sync.modus': 'Modalità',
    'sync.offline': 'Offline',
    'sync.laatste_sync': 'Ultima sync',
    'sync.nooit': 'Mai',
    'sync.zojuist': 'Proprio ora',
    'sync.geleden': 'fa',
    'sync.wachtende': 'In attesa',
    'sync.wijziging': 'modifica',
    'sync.wijzigingen': 'modifiche',
    'sync.nu_sync': 'Sync ora',
    'sync.inklappen': 'Comprimi',
    'sync.uitloggen': 'Disconnetti',
    'common.opslaan': 'Salva',
    'common.opslaan_ellipsis': 'Salvataggio...',
    'common.opgeslagen': 'Salvato!',
    'common.annuleren': 'Annulla',
    'common.toevoegen': 'Aggiungi',
    'common.bewerken': 'Modifica',
    'common.verwijderen': 'Elimina',
    'common.actief': 'Attivo',
    'common.inactief': 'Inattivo',
    'common.nieuw': 'Nuovo',
    'common.zoeken': 'Cerca',
    'common.laden': 'Caricamento...',
    'common.sluiten': 'Chiudi',
    'common.bevestigen': 'Conferma',
    'common.ja': 'Sì',
    'common.nee': 'No',
    'common.taal': 'Lingua',
    'common.talen': 'Lingue',
    'settings.bedrijfsprofiel': 'Profilo Aziendale',
    'settings.zones': 'Zone',
    'settings.apparaten': 'Dispositivi',
    'settings.categorieen': 'Categorie',
    'settings.rollen': 'Ruoli del Personale',
    'settings.ayce': 'Impostazioni AYCE',
    'settings.btw': 'Gruppi IVA',
    'settings.verbinding': 'Connessione & Sync',
    'settings.talen': 'Lingue & Traduzioni',
    'lang.title': 'Lingue & Traduzioni',
    'lang.subtitle': 'Gestisci le impostazioni della lingua per il software e il menu.',
    'lang.ui_title': 'Lingua del Software',
    'lang.ui_desc': 'Scegli la lingua per tutti i pulsanti, le etichette e la navigazione nel software.',
    'lang.content_title': 'Traduzioni del Menu',
    'lang.content_desc': 'Abilita la traduzione automatica di nomi e descrizioni dei prodotti sul menu digitale.',
    'lang.content_toggle': 'Abilita traduzioni del menu',
    'lang.content_enabled': 'Abilitato — i clienti possono scegliere la loro lingua sul menu digitale',
    'lang.content_disabled': 'Disabilitato — il menu viene mostrato solo nella lingua predefinita',
    'lang.translate_btn': 'Genera traduzioni',
    'lang.translating': 'Traduzione...',
    'lang.translated': 'Traduzioni salvate!',
    'lang.translate_desc': 'Clicca per tradurre automaticamente tutti i nomi e le descrizioni dei prodotti con l\'IA.',
    'lang.select_ui': 'Seleziona lingua del software',
    'lang.enabled_langs': 'Lingue disponibili per i clienti',
    'lang.default_lang': 'Lingua predefinita',
    'lang.save_settings': 'Salva impostazioni',
    'ayce.menu': 'Menu',
    'ayce.bestelling': 'Ordine',
    'ayce.overzicht': 'Panoramica',
    'ayce.rekening': 'Conto',
    'ayce.service': 'Servizio',
    'ayce.ronde': 'Giro',
    'ayce.bestellen': 'Ordinare',
    'ayce.indienen': 'Invia giro',
    'ayce.indienen_ellipsis': 'Invio...',
    'ayce.alles': 'Tutto',
    'ayce.populair': 'Popolare',
    'ayce.max_een': 'Max. 1',
    'ayce.notitie': 'Nota',
    'ayce.notitie_placeholder': 'Es. senza cipolla, ben cotto...',
    'ayce.toevoegen': 'Aggiungi',
    'ayce.verwijderen': 'Rimuovi',
    'ayce.cooldown': 'Prossimo giro tra',
    'ayce.max_rondes': 'Giri massimi raggiunti',
    'ayce.sessie_verlopen': 'Sessione scaduta',
    'ayce.sessie_actief': 'Sessione attiva',
    'ayce.tafel': 'Tavolo',
    'ayce.gasten': 'ospiti',
    'ayce.welkom': 'Benvenuto!',
    'ayce.geen_sessie': 'Nessuna sessione attiva',
    'ayce.ongeldige_qr': 'Codice QR non valido o scaduto. Chiedi aiuto a un membro del personale.',
    'ayce.service_verzoek': 'Richiesta di servizio',
    'ayce.service_verzonden': 'Richiesta inviata!',
    'ayce.lege_bestelling': 'Aggiungi prodotti al tuo ordine',
    'ayce.bestelling_ingediend': 'Ordine inviato!',
    'ayce.rondes_overzicht': 'Panoramica giri',
    'ayce.geen_rondes': 'Nessun giro ordinato',
    'ayce.rekening_totaal': 'Totale',
    'ayce.rekening_per_gast': 'Per ospite',
    'ayce.rekening_info': 'Il conto verrà portato dal tuo cameriere.',
    'ayce.min_producten': 'Minimo {n} prodotti richiesti',
    'ayce.max_producten': 'Massimo {n} prodotti raggiunto',
    'ayce.dranken_onbeperkt': 'Le bevande sono illimitate',
    'ayce.sessie_tijd': 'Durata sessione',
    'ayce.tijd_over': 'rimanente',
    // Menu Preview (guest-facing)
    'menu.menukaart': 'Menu',
    'menu.alles': 'Tutto',
    'menu.winkelwagen': 'Carrello',
    'menu.bestellingen': 'Ordini',
    'menu.service': 'Servizio',
    'menu.toevoegen': 'Aggiungi',
    'menu.bestellen': 'Ordinare',
    'menu.totaal': 'Totale',
    'menu.leeg': 'Il tuo carrello è vuoto',
    'menu.populair': 'Popolare',
    'menu.aanbevolen': '★ Consigliato',
    'menu.opmerking': 'Nota (opzionale)',
    'menu.opmerking_placeholder': 'Es. senza cipolla, ben cotto...',
    'menu.bestelling_plaatsen': 'Effettua ordine',
    'menu.bestelling_geplaatst': 'Ordine effettuato!',
    'menu.service_verzoek': 'Richiesta di servizio',
    'menu.service_verzonden': 'Richiesta inviata!',
    'menu.geen_bestellingen': 'Nessun ordine ancora',
    'menu.status_pending': 'In attesa',
    'menu.status_preparing': 'In preparazione',
    'menu.status_ready': 'Pronto',
    'menu.status_served': 'Servito',
    // Service items
    'service.water': 'Acqua',
    'service.bestek': 'Posate extra',
    'service.servet': 'Tovagliolo',
    'service.rekening': 'Conto',
    'service.kinderstoel': 'Seggiolone',
    'service.saus': 'Salsa extra',
    'service.ijs': 'Ghiaccio extra',
    'service.stokjes': 'Bacchette',
    'service.asbak': 'Posacenere',
    // KDS - Kitchen Display System
    'kds.title': 'Display Cucina',
    'kds.subtitle': 'Sistema Display Cucina',
    'kds.tafel': 'Tavolo',
    'kds.ronde': 'Giro',
    'kds.gasten': 'ospiti',
    'kds.couverts': 'coperti',
    'kds.status_new': 'Nuovo',
    'kds.status_in_progress': 'In corso',
    'kds.status_ready': 'Pronto',
    'kds.status_done': 'Fatto',
    'kds.start': 'Avvia',
    'kds.totaal_klaar': '✓ Tutto Pronto',
    'kds.klaar': '✓ Pronto',
    'kds.urgent': '⚠ URGENTE',
    'kds.terugzetten': 'Ripristina',
    'kds.teruggezet': 'Ripristinato',
    'kds.items_klaar': 'articoli pronti',
    'kds.geen_bestellingen': 'Nessun ordine',
    'kds.geen_actieve': 'Al momento non ci sono ordini attivi',
    'kds.geen_geschiedenis': 'Nessuna cronologia',
    'kds.afgeronde_hier': 'Gli ordini completati appaiono qui',
    'kds.wacht_lang': '⚠ Attesa lunga',
    'kds.afgehandeld': '✓ Gestito',
    'kds.service': 'Servizio',
    'kds.rondes': 'Giri',
    'kds.wacht_bevestiging': 'In attesa di conferma cucina',
    'kds.in_bereiding': 'In preparazione',
    'kds.ronde_klaar': '✓ Giro completato',
    'kds.ronde_bevestigd': '✓ Giro confermato',
    'kds.wacht_keuken': 'in attesa cucina',
    'kds.bevestigd': 'Confermato',
    'kds.geen_ayce_rondes': 'Nessun giro AYCE',
    'kds.ayce_rondes_hier': 'I giri AYCE attivi appaiono qui',
    'kds.geen_service': 'Nessuna richiesta di servizio',
    'kds.service_hier': 'Le richieste degli ospiti appaiono qui',
    'kds.wachten': 'In attesa',
    'kds.totaal_actief': 'Totale attivo',
    'kds.urgentie': 'Urgenza:',
    'kds.terugzetten_tip': 'Clicca ↺ per ripristinare un prodotto o un ticket',
    'kds.geen_restaurant': 'Nessun ristorante selezionato',
    'kds.volgende_ronde': 'Prossimo giro disponibile dopo',
    'kds.wachttijd': 'min. di attesa (dall\'invio)',
    'kds.ingediend_om': 'Inviato alle',
    'kds.gedrukt': 'Stampato',
    'kds.ayce_keuken': 'Cucina AYCE',
    'kds.route': 'Percorso',
    'kds.bon_printen': '🖨 Stampa Ticket',
    'kds.kies_station': 'Scegli stazione per il ticket:',
    'kds.print': 'Stampa',
    'kds.items': 'articoli',
    'kds.rondes_open': 'Giri aperti',
    'kds.items_te_maken': 'Articoli da preparare',
    'kds.groepeer': '📦 Raggruppa',
    'kds.groepeer_weergave': 'Vista raggruppata',
    'kds.groepeer_desc': 'I piatti identici di tutti i tavoli vengono combinati. Preparali in una volta sola e segna tutto come pronto.',
    'kds.groepeer_desc_kds': 'Solo gli ordini avviati vengono raggruppati — i nuovi ordini non si uniscono mai a un gruppo attivo.',
    'kds.groepeer_start_eerst': 'Avvia prima un ordine per raggruppare',
    'kds.bestellingen': 'Ordini',
    'kds.niets_groeperen': 'Niente da raggruppare',
    'kds.geen_openstaande': 'Nessun giro in sospeso',
    'kds.alle_verwerkt': 'Tutti i giri elaborati',
    'kds.geen_orders_station': 'Nessun ordine per',
    'kds.station': 'stazione',
    'kds.klaar_zichtbaar': '✓ Fatto visibile',
    'kds.klaar_verborgen': 'Fatto nascosto',
    'kds.tafels': 'tavoli',
    'kds.alles_klaar': '✓ Tutto Pronto',
    'kds.bezig': 'In corso',
    'kds.nieuw': 'Nuovo',
    'kds.alle': 'Tutto',
    'kds.geschiedenis': 'Cronologia',
    'kds.ayce': 'AYCE',
    'kds.service_verzoek': 'Servizio',
    'kds.geleden': 'fa',
    'kds.min': 'min',
    'kds.allergen_warning': 'Allergeni presenti',
    'kds.prep_instructions': 'Istruzioni di preparazione',
    'kds.order_number': 'Numero ordine',
  },
};

// ─── Translation function ─────────────────────────────────────────────────────
export function t(key: string, language: LanguageCode = 'nl', params?: Record<string, string | number>): string {
  const langDict = translations[language] || translations['nl'];
  let text = langDict[key] || translations['nl'][key] || key;
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(`{${k}}`, String(v));
    });
  }
  return text;
}

// ─── Language settings storage ────────────────────────────────────────────────
const UI_LANG_KEY = 'horecapos_ui_language';
const CONTENT_LANG_KEY = 'horecapos_content_translation_enabled';
const GUEST_LANGS_KEY = 'horecapos_guest_languages';

export function loadUILanguage(): LanguageCode {
  if (typeof window === 'undefined') return 'nl';
  return (localStorage.getItem(UI_LANG_KEY) as LanguageCode) || 'nl';
}

export function saveUILanguage(lang: LanguageCode): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(UI_LANG_KEY, lang);
}

export function loadContentTranslationEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(CONTENT_LANG_KEY) === 'true';
}

export function saveContentTranslationEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(CONTENT_LANG_KEY, String(enabled));
}

export function loadGuestLanguages(): LanguageCode[] {
  if (typeof window === 'undefined') return ['nl'];
  try {
    const stored = localStorage.getItem(GUEST_LANGS_KEY);
    if (stored) return JSON.parse(stored) as LanguageCode[];
  } catch {}
  return ['nl', 'en'];
}

export function saveGuestLanguages(langs: LanguageCode[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(GUEST_LANGS_KEY, JSON.stringify(langs));
}
